/**
 * 🐦 Guild Hall — Phase 1 Foundation page (Feb 2026)
 *
 * - No guild yet: shows Create + Browse tabs.
 * - In a guild: shows member list, wealth panel, contribute panel, and
 *   leader-only actions (promote/demote/kick/transfer/rename).
 *
 * All data flows through /api/guild/state. Mutations trigger a refresh.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import GuildLeverPanel from "@/components/GuildLeverPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_BADGE = {
  leader:           { icon: "👑", label: "Leader",          color: "#FCD34D" },
  assistant_leader: { icon: "🪶", label: "Assistant Leader", color: "#A855F7" },
  officer:          { icon: "⭐", label: "Officer",          color: "#60A5FA" },
  member:           { icon: "🐦", label: "Member",           color: "#94A3B8" },
};

// 🛡️ Defensive defaults for the `constants` block — guards against schema
// drift between FE build and any older deployed backend. If the backend ever
// omits a field, FE keeps rendering instead of crashing to a blank page.
const CONSTANTS_DEFAULTS = {
  default_name: "Birds of a Feather",
  founding_cost_chits: 20000,
  name_min_chars: 4,
  name_max_chars: 18,
  rename_cost_od: 1000,
  member_cap_base: 10,
  max_member_cap: 50,       // 🔒 owner-locked ceiling (Feb 18 2026)
  officer_max: 3,
  wealth_per_chits: 1000,
  wealth_per_od: 5,
};

// 🚨 Panic-leave: always-available emergency button that calls /api/guild/leave
// directly, bypassing the React tree. Used when the rest of the page crashes.
async function panicLeave() {
  if (!window.confirm("Force-leave the guild now? (Emergency exit — bypasses any UI errors.)")) return;
  try {
    await axios.post(`${API}/guild/leave`);
    toast.success("👋 Force-left the guild. Reloading...");
    setTimeout(() => window.location.reload(), 800);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Force-leave failed — reach out to support.");
  }
}

// 🚨 Tiny error boundary so a render crash inside this page never blanks the screen.
class GuildErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    console.error("[GuildPage crashed]", err, info);
  }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen bg-black text-neutral-200 px-4 py-12 flex items-center justify-center"
             data-testid="guild-page-crash">
          <div className="max-w-md w-full rounded-2xl border border-red-700/50 bg-red-950/40 p-6 text-center">
            <div className="text-4xl mb-3">⚠️</div>
            <h2 className="text-xl font-black text-red-100 mb-2">Guild Hall hit an error</h2>
            <p className="text-xs text-red-200/80 mb-5 break-words">
              {String(this.state.err?.message || this.state.err)}
            </p>
            <div className="flex gap-2 flex-col">
              <button onClick={panicLeave}
                      data-testid="guild-panic-leave"
                      className="w-full px-4 py-3 rounded-lg bg-red-600 hover:bg-red-500 text-white font-black tracking-wider">
                🚪 FORCE LEAVE GUILD
              </button>
              <Link to="/town-hall"
                    className="w-full block px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-amber-200 font-bold text-sm">
                ← Back to Town Hall
              </Link>
              <button onClick={() => window.location.reload()}
                      className="w-full px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-neutral-200 text-sm">
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function GuildPage() {
  return (
    <GuildErrorBoundary>
      <GuildPageInner />
    </GuildErrorBoundary>
  );
}

function GuildPageInner() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [browse, setBrowse] = useState([]);
  const [tab, setTab] = useState("create");  // create | browse  (only when no guild)
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!isAuthed) return;
    try {
      const r = await axios.get(`${API}/guild/state`);
      setState(r.data);
      if (!r.data.in_guild) {
        const r2 = await axios.get(`${API}/guild/browse`);
        setBrowse(r2.data?.guilds || []);
      }
    } catch (e) {
      toast.error("Failed to load Guild Hall.");
    }
  };
  useEffect(() => { load(); }, [isAuthed]);

  const post = async (path, body, successMsg) => {
    setBusy(true);
    try {
      await axios.post(`${API}/guild/${path}`, body || {});
      if (successMsg) toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0b0014] via-black to-[#1a0018] text-neutral-100 px-4 py-10"
           data-testid="guild-page-logged-out">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400/70 mb-2">Town Hall · Section</div>
          <h1 className="text-3xl sm:text-5xl font-black text-amber-200 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
            🐦 BIRDS OF A FEATHER
          </h1>
          <div className="text-base text-neutral-400 italic mb-6">
            "No flock without first proving you're a bird."
          </div>
          <div className="rounded-2xl bg-black/60 border border-amber-700/40 p-6">
            <p className="text-sm text-neutral-300 mb-5">
              Sign in to found your own guild, join the flock, or browse other heroes' nests.
            </p>
            <Link to="/auth"
                  data-testid="guild-login-cta"
                  onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("omni:open-auth")); }}
                  className="inline-block px-6 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-400 hover:to-amber-600 text-black font-black tracking-wider">
              🪶 SIGN IN TO JOIN
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-amber-100"
           data-testid="guild-page-loading">
        <div className="animate-pulse text-2xl">🐦 Gathering the flock...</div>
      </div>
    );
  }

  // 🛡️ Backfill any missing constants from defaults so legacy backends
  // never crash the page.
  const C = { ...CONSTANTS_DEFAULTS, ...(state.constants || {}) };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0b0014] via-black to-[#1a0018] text-neutral-100 px-4 py-6 sm:py-10">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400/70">Town Hall · Section</div>
            <h1 className="text-3xl sm:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🐦 BIRDS OF A FEATHER
            </h1>
            <div className="text-xs sm:text-sm text-neutral-400 mt-1">Find your flock. Share the sky.</div>
          </div>
          <div className="flex items-center gap-2">
            {state.in_guild && (
              <button onClick={panicLeave}
                      data-testid="guild-header-leave"
                      title="Emergency leave (works even if guild data is stuck)"
                      className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-red-900/30 hover:bg-red-900/60 border border-red-700/40 text-red-200">
                🚪 Leave
              </button>
            )}
            <Link to="/town-hall" data-testid="guild-back-link"
                  className="text-xs uppercase tracking-widest text-amber-300/70 hover:text-amber-200">
              ← Town Hall
            </Link>
          </div>
        </div>

        {!state.in_guild
          ? <NoGuildPanel
              tab={tab} setTab={setTab}
              constants={C} state={state} browse={browse}
              busy={busy} post={post} />
          : <InGuildPanel
              state={state} constants={C} busy={busy} post={post} reload={load} />}
      </div>
    </div>
  );
}

/* ─────────────── Panels ─────────────── */

// 🚪 Live rejoin countdown banner — Roc #29.
// Counts down to the moment the user can join a guild again. Updates every
// second so the HH:MM:SS reads honestly.
function RejoinCooldownBanner({ cd }) {
  const [remaining, setRemaining] = useState(cd?.seconds_remaining || 0);
  useEffect(() => {
    setRemaining(cd?.seconds_remaining || 0);
    if (!cd?.seconds_remaining) return;
    const t = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cd?.seconds_remaining, cd?.cooldown_until]);
  if (remaining <= 0) return null;
  const hh = Math.floor(remaining / 3600);
  const mm = Math.floor((remaining % 3600) / 60);
  const ss = remaining % 60;
  const pct = cd?.hours_total
    ? Math.max(0, Math.min(100, (remaining / (cd.hours_total * 3600)) * 100))
    : 0;
  return (
    <div className="mb-5 rounded-lg border border-red-700/40 bg-red-950/30 p-3"
         data-testid="guild-rejoin-cooldown-banner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-red-200">
          <span className="font-black uppercase tracking-widest">⏳ Rejoin on cooldown</span>
          <span className="text-neutral-400 ml-2">
            — you can join a guild again in
          </span>
        </div>
        <div className="font-mono text-base text-red-100 font-bold tabular-nums"
             data-testid="guild-rejoin-cooldown-timer">
          {String(hh).padStart(2, "0")}:{String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
        </div>
      </div>
      <div className="mt-2 h-1 bg-black/60 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-red-700 to-red-400 transition-all"
             style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function NoGuildPanel({ tab, setTab, constants, state, browse, busy, post }) {
  const [name, setName] = useState("");
  const chits = state?.wallet?.chits || 0;
  const canAfford = chits >= constants.founding_cost_chits;

  return (
    <div className="bg-black/50 border border-amber-900/40 rounded-xl p-4 sm:p-6">
      {/* 🚪 Guild rejoin cooldown banner (Roc #29) */}
      {state?.rejoin_cooldown && <RejoinCooldownBanner cd={state.rejoin_cooldown} />}
      <div className="flex gap-2 mb-5">
        <TabBtn active={tab === "create"} onClick={() => setTab("create")} testid="guild-tab-create">🏗️ Create</TabBtn>
        <TabBtn active={tab === "browse"} onClick={() => setTab("browse")} testid="guild-tab-browse">🔍 Browse ({browse.length})</TabBtn>
      </div>

      {tab === "create" && (
        <div data-testid="guild-create-panel">
          <div className="text-sm text-neutral-400 mb-4">
            Found your own flock. Costs <span className="text-amber-300 font-black">{constants.founding_cost_chits.toLocaleString()} 🪙 Chits</span>.
            Leave blank to use the default name <span className="italic">"{constants.default_name}"</span>.
          </div>
          <input
            type="text"
            placeholder={constants.default_name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={constants.name_max_chars}
            className="w-full bg-black/60 border border-amber-700/50 rounded px-3 py-2 text-amber-100 placeholder-neutral-500 focus:outline-none focus:border-amber-400"
            data-testid="guild-create-name"
          />
          <div className="text-[10px] text-neutral-500 mt-1">
            {constants.name_min_chars}–{constants.name_max_chars} characters · letters / numbers / spaces / - _ '
          </div>
          <button
            type="button"
            disabled={busy || !canAfford}
            onClick={() => post("create", { name: name.trim() || null }, "🐦 Guild founded!")}
            data-testid="guild-create-submit"
            className={`mt-4 w-full font-black tracking-widest py-3 rounded transition ${
              !canAfford
                ? "bg-neutral-900 text-neutral-600 cursor-not-allowed"
                : "bg-amber-500 hover:bg-amber-400 text-black"
            }`}
          >
            {canAfford
              ? `FOUND GUILD — ${constants.founding_cost_chits.toLocaleString()} 🪙`
              : `Need ${(constants.founding_cost_chits - chits).toLocaleString()} more 🪙`}
          </button>
          <div className="mt-2 text-xs text-neutral-500">Your wallet: 🪙 {chits.toLocaleString()} Chits</div>
        </div>
      )}

      {tab === "browse" && (
        <div className="space-y-2" data-testid="guild-browse-panel">
          {browse.length === 0 && (
            <div className="text-center text-neutral-500 py-12">
              <div className="text-4xl mb-2">🪶</div>
              <div>No guilds yet. Be the first to build a flock.</div>
            </div>
          )}
          {browse.map((g) => (
            <div key={g.guild_id}
                 className="flex items-center gap-3 bg-black/40 border border-neutral-800 hover:border-amber-700/60 rounded-lg p-3 transition"
                 data-testid={`guild-browse-row-${g.guild_id}`}>
              <div className="text-2xl">🐦</div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-amber-100 truncate" style={{ fontFamily: "Anton, sans-serif" }}>
                  {g.name}
                </div>
                <div className="text-[10px] text-neutral-400">
                  Leader: <span className="text-neutral-200">{g.leader_name}</span> ·
                  Hall Lv {g.hall_level} · {g.member_count}/{g.member_cap} members
                </div>
              </div>
              <button
                type="button"
                disabled={busy || !g.open}
                onClick={() => post("join", { guild_id: g.guild_id }, "🐦 Joined the flock!")}
                data-testid={`guild-join-${g.guild_id}`}
                className={`text-xs uppercase tracking-wider font-bold px-3 py-2 rounded transition ${
                  g.open ? "bg-amber-500 hover:bg-amber-400 text-black" : "bg-neutral-800 text-neutral-500"
                }`}
              >
                {g.open ? "Join" : "Full"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InGuildPanel({ state, constants, busy, post, reload }) {
  const g = state.guild;
  const isLeader = state.role === "leader";
  const [renameOpen, setRenameOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [chitsToConvert, setChitsToConvert] = useState(0);
  const [odToConvert, setOdToConvert] = useState(0);

  const handleContribute = () => {
    if (!chitsToConvert && !odToConvert) {
      toast.error("Enter Chits or OD to convert.");
      return;
    }
    post("contribute",
      { chits: chitsToConvert, od: odToConvert },
      `+${(chitsToConvert / constants.wealth_per_chits) + (odToConvert / constants.wealth_per_od)} Wealth gained!`);
    setChitsToConvert(0);
    setOdToConvert(0);
  };

  return (
    <div className="space-y-5">
      {/* Guild banner */}
      <div className="bg-gradient-to-r from-amber-950/40 via-purple-950/30 to-black/60 border border-amber-700/40 rounded-xl p-4 sm:p-6"
           data-testid="guild-banner">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/70">Hall Lv {g.hall_level} · {g.member_count}/{g.member_cap} flock</div>
            <h2 className="text-2xl sm:text-4xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
              🐦 {g.name}
            </h2>
            {g.rename_days_remaining > 0 && (
              <div className="text-[10px] text-neutral-500 mt-1">Rename available in {g.rename_days_remaining} days</div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {isLeader && (
              <button type="button" onClick={() => setRenameOpen(!renameOpen)}
                      data-testid="guild-rename-toggle"
                      className="text-xs uppercase tracking-wider font-bold px-3 py-2 bg-amber-700/30 hover:bg-amber-700/50 text-amber-200 rounded border border-amber-700/40">
                ✏️ Rename
              </button>
            )}
          </div>
        </div>

        {renameOpen && isLeader && (
          <div className="mt-4 pt-4 border-t border-amber-900/30 flex items-center gap-2 flex-wrap"
               data-testid="guild-rename-form">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={constants.name_max_chars}
              placeholder={`${constants.name_min_chars}-${constants.name_max_chars} chars`}
              className="flex-1 min-w-[160px] bg-black/60 border border-amber-700/50 rounded px-3 py-2 text-amber-100 placeholder-neutral-500"
              data-testid="guild-rename-input"
            />
            <button type="button" disabled={busy || g.rename_days_remaining > 0}
                    onClick={() => post("rename", { name: newName }, `Renamed to "${newName}"!`)}
                    data-testid="guild-rename-submit"
                    className="text-xs uppercase tracking-wider font-bold px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black rounded disabled:bg-neutral-800 disabled:text-neutral-500">
              Pay {constants.rename_cost_od} OD
            </button>
            <div className="text-[10px] text-neutral-500 w-full">
              Your OD: {Number(state.wallet?.od || 0).toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* 🏛️ Guild Hall Ladders pointer */}
      <Link
        to="/guild/hall"
        data-testid="guild-hall-ladders-pointer"
        className="block rounded-xl border-2 border-emerald-700/50 bg-gradient-to-br from-[#001a14] via-black/60 to-emerald-900/15 p-4 sm:p-5 hover:border-emerald-500/80 hover:shadow-[0_0_30px_-12px_rgba(16,185,129,0.6)] transition-all group"
      >
        <div className="flex items-center gap-4">
          <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">🏛️</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">Visit · Canon Ladders</div>
            <div className="text-xl sm:text-2xl font-black text-emerald-200" style={{ fontFamily: "Anton, sans-serif" }}>
              GUILD HALL LADDERS
            </div>
            <p className="text-xs text-emerald-100/80 mt-1">
              Shop · Skill Tower · Divine Altar · Weekly Upkeep — see every upgrade tier, next-cost vs. your wealth, and weeks of upkeep cushion.
            </p>
          </div>
          <div className="text-emerald-300 text-2xl flex-shrink-0 group-hover:translate-x-1 transition-transform">→</div>
        </div>
      </Link>

      {/* 🗼 Ashen Apex pointer */}
      <Link
        to="/guild/ashen-apex"
        data-testid="guild-apex-pointer"
        className="block rounded-xl border-2 border-amber-700/50 bg-gradient-to-br from-[#1a0500] via-black/60 to-amber-900/15 p-4 sm:p-5 hover:border-amber-500/80 hover:shadow-[0_0_30px_-12px_rgba(245,158,11,0.6)] transition-all group"
      >
        <div className="flex items-center gap-4">
          <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">🗼</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Visit · Phase 1 Preview</div>
            <div className="text-xl sm:text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              THE ASHEN APEX
            </div>
            <p className="text-xs text-amber-100/80 mt-1">
              The guild's skill tower. Five disciplines, ten ashen rungs · upgrade ladders + per-skill costs visible now, purchases land in upcoming phases.
            </p>
          </div>
          <div className="text-amber-300 text-2xl flex-shrink-0 group-hover:translate-x-1 transition-transform">→</div>
        </div>
      </Link>

      {/* 🔷 Skill Shards pointer — guild-wide upgrade tracks */}
      <Link
        to="/guild/skill-shards"
        data-testid="guild-skill-shards-pointer"
        className="block rounded-xl border-2 border-sky-700/50 bg-gradient-to-br from-[#001428] via-black/60 to-purple-900/15 p-4 sm:p-5 hover:border-sky-500/80 hover:shadow-[0_0_30px_-12px_rgba(56,189,248,0.6)] transition-all group"
      >
        <div className="flex items-center gap-4">
          <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">🔷</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-sky-300/80">Visit · Officer Upgrades</div>
            <div className="text-xl sm:text-2xl font-black text-sky-200" style={{ fontFamily: "Anton, sans-serif" }}>
              SKILL SHARDS
            </div>
            <p className="text-xs text-sky-100/80 mt-1">
              Six guild-wide upgrade tracks · Power · Intellect · Defense · Endurance · Charisma · Chit Finder. Buffs apply to every member while in the guild. Cap rises with the Ashen Apex.
            </p>
          </div>
          <div className="text-sky-300 text-2xl flex-shrink-0 group-hover:translate-x-1 transition-transform">→</div>
        </div>
      </Link>

      {/* 🎡 Guild Lever — daily random-item pull */}
      <GuildLeverPanel onWealthChanged={reload} />

      {/* 🌑 Chronophage Guild Boss — Coming Soon tile */}
      <Link
        to="/guild/chronophage"
        data-testid="guild-chronophage-pointer"
        className="block rounded-xl border-2 border-violet-700/50 bg-gradient-to-br from-[#1B0B2E] via-black/60 to-purple-900/25 p-4 sm:p-5 hover:border-violet-500/80 hover:shadow-[0_0_30px_-12px_rgba(139,92,246,0.6)] transition-all group"
      >
        <div className="flex items-center gap-4">
          <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">🌑</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-violet-300/80">Guild Boss · Reserved Slot</div>
            <div className="text-xl sm:text-2xl font-black text-violet-200" style={{ fontFamily: "Anton, sans-serif" }}>
              CHRONOPHAGE ATTACK
            </div>
            <p className="text-xs text-violet-100/80 mt-1">
              The Devourer of Hours stirs... a guild-wide raid with its own bespoke economy. ⏳ Coming Soon.
            </p>
          </div>
          <div className="text-violet-300 text-2xl flex-shrink-0 group-hover:translate-x-1 transition-transform">→</div>
        </div>
      </Link>

      {/* Wealth panel */}
      <div className="bg-black/50 border border-purple-900/40 rounded-xl p-4 sm:p-6" data-testid="guild-wealth-panel">
        <h3 className="text-lg font-black text-purple-200 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
          💎 GUILD WEALTH (PERSONAL)
        </h3>
        <div className="flex items-center gap-6 mb-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Your Balance</div>
            <div className="text-3xl font-black text-purple-300" data-testid="guild-wealth-balance">💎 {Number(state.your_wealth || 0).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">This Guild · Lifetime</div>
            <div className="text-xl font-black text-purple-400/70 flex items-center gap-1.5" data-testid="guild-your-lifetime-contrib">
              {Number(state.your_guild_lifetime_contrib || 0).toLocaleString()}
              {(state.your_chits_badges || []).map(b => (
                <span key={b.id}
                      data-testid={`guild-badge-${b.id}`}
                      title={`${b.label} — ${Number(b.chits).toLocaleString()}+ 🪙 Chits contributed to this guild`}
                      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-amber-500/20 border border-amber-400/60 text-amber-200">
                  {b.icon} {b.label}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Today (UTC)</div>
            <div className="text-xl font-black text-emerald-300/80" data-testid="guild-your-daily-contrib">
              +{Number(state.your_daily_contrib || 0).toLocaleString()} 💎
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Pool Wealth</div>
            <div className="text-xl font-black text-amber-300" data-testid="guild-pool-wealth">
              {Number(g.pool_wealth || 0).toLocaleString()} 💎
            </div>
            <div className="text-[9px] text-neutral-500 mt-0.5">
              Lifetime contributed: {Number(g.total_wealth_contributed || 0).toLocaleString()} 💎
            </div>
            <BadgeProgress yourLifetime={Number(state.your_guild_lifetime_contrib || 0)} />
          </div>
        </div>
        <div className="text-[10px] text-neutral-500 mb-3">
          Convert {constants.wealth_per_chits.toLocaleString()} 🪙 Chits = 1 💎 · {constants.wealth_per_od} OD = 1 💎.
          Contributions feed the pool — pool funds building upgrades, controlled by the Leader / Assistant.
        </div>

        {/* Chits-on-hand indicator + live preview */}
        <div className="mb-3 p-3 rounded-lg bg-black/40 border border-amber-700/30 flex items-center justify-between gap-3 flex-wrap"
             data-testid="guild-contribute-onhand">
          <div className="text-xs">
            <span className="text-neutral-400 uppercase tracking-wider text-[10px]">On hand:</span>
            <span className="ml-2 font-black text-amber-300" data-testid="guild-onhand-chits">
              🪙 {Number(state.wallet?.chits || 0).toLocaleString()} Chits
            </span>
            <span className="ml-3 font-black text-amber-200/80" data-testid="guild-onhand-od">
              💵 {Number(state.wallet?.od || 0).toLocaleString()} OD
            </span>
          </div>
          <div className="text-xs">
            <span className="text-neutral-400 uppercase tracking-wider text-[10px]">Will gain:</span>
            <span className="ml-2 font-black text-purple-200" data-testid="guild-contribute-preview">
              💎 {(
                Math.floor((Number(chitsToConvert) || 0) / constants.wealth_per_chits) +
                Math.floor((Number(odToConvert) || 0) / constants.wealth_per_od)
              ).toLocaleString()} Wealth
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input type="number" min="0" step={constants.wealth_per_chits}
                 placeholder={`Chits (×${constants.wealth_per_chits.toLocaleString()})`}
                 value={chitsToConvert || ""}
                 max={state.wallet?.chits || undefined}
                 onChange={(e) => setChitsToConvert(Math.max(0, parseInt(e.target.value) || 0))}
                 className="bg-black/60 border border-purple-700/50 rounded px-3 py-2 text-purple-100 placeholder-neutral-500"
                 data-testid="guild-contribute-chits" />
          <input type="number" min="0" step={constants.wealth_per_od}
                 placeholder={`OD (×${constants.wealth_per_od})`}
                 value={odToConvert || ""}
                 max={state.wallet?.od || undefined}
                 onChange={(e) => setOdToConvert(Math.max(0, parseInt(e.target.value) || 0))}
                 className="bg-black/60 border border-purple-700/50 rounded px-3 py-2 text-purple-100 placeholder-neutral-500"
                 data-testid="guild-contribute-od" />
        </div>
        <button type="button" disabled={busy || (!chitsToConvert && !odToConvert)}
                onClick={handleContribute}
                data-testid="guild-contribute-submit"
                className="mt-3 w-full font-black tracking-widest py-2 bg-purple-600 hover:bg-purple-500 text-white rounded disabled:bg-neutral-800 disabled:text-neutral-500">
          💎 CONFIRM &amp; CONVERT
        </button>
      </div>

      {/* Leaderboard panel — daily + lifetime, THIS guild only */}
      <ContribLeaderboard guild={g} />

      {/* Member list */}
      <div className="bg-black/50 border border-neutral-800 rounded-xl p-4 sm:p-6" data-testid="guild-members-panel">
        <h3 className="text-lg font-black text-amber-200 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
          🪶 THE FLOCK ({g.member_count}/{g.member_cap})
        </h3>
        <div className="space-y-2">
          {(g.members || []).map((m) => (
            <MemberRow key={m.user_id} m={m} isLeader={isLeader}
                       constants={constants} busy={busy} post={post} g={g} />
          ))}
        </div>
      </div>

      {/* 🚪 Leave Guild — bottom footer, requires intent */}
      <div className="pt-6 mt-4 border-t border-red-900/30 flex flex-col sm:flex-row items-center justify-between gap-3"
           data-testid="guild-leave-footer">
        <div className="text-[11px] text-neutral-500 italic font-serif">
          {isLeader
            ? "As Leader, leaving auto-transfers the crown (or dissolves the guild if you're solo). You'll be locked out of ANY guild for 24 hours after leaving."
            : "Leaving forfeits your contribution counter and locks you out of ANY guild for 24 hours. You can rejoin after the cooldown; your current Wealth share resets either way."}
        </div>
        <button type="button" disabled={busy}
                onClick={() => {
                  if (window.confirm(isLeader
                    ? "Leave? Leadership will auto-transfer (or guild dissolves if you're solo).\n\n⏳ You'll be locked out of ANY guild for 24 hours."
                    : "Leave the guild?\n\n⏳ You'll be locked out of ANY guild for 24 hours after leaving.")) {
                    post("leave", null, "👋 Left the guild.");
                  }
                }}
                data-testid="guild-leave-btn"
                className="text-xs uppercase tracking-wider font-bold px-4 py-2 bg-red-900/30 hover:bg-red-900/60 text-red-200 rounded border border-red-700/40 flex-shrink-0">
          🚪 Leave Guild
        </button>
      </div>
    </div>
  );
}


// 🎖️ Live badge progress bar — shows your lifetime contribution toward
// the next contribution-milestone badge for THIS guild.
const CONTRIB_BADGES = [
  { id: "first_drop",       threshold: 100,     icon: "💧", label: "First Drop" },
  { id: "ten_k_club",       threshold: 10_000,  icon: "💰", label: "10,000 Club" },
  { id: "love_of_the_flock", threshold: 100_000, icon: "💞", label: "Love of the Flock" },
];

function BadgeProgress({ yourLifetime }) {
  const lifetime = Math.max(0, Number(yourLifetime) || 0);
  // Find the next unearned badge; if all earned, show the final one as complete.
  const next = CONTRIB_BADGES.find((b) => lifetime < b.threshold) || CONTRIB_BADGES[CONTRIB_BADGES.length - 1];
  // Floor of this tier = the previous tier's threshold (or 0).
  const idx = CONTRIB_BADGES.indexOf(next);
  const floor = idx > 0 ? CONTRIB_BADGES[idx - 1].threshold : 0;
  const span = next.threshold - floor;
  const pct = span > 0 ? Math.max(0, Math.min(100, ((lifetime - floor) / span) * 100)) : 100;
  const done = lifetime >= next.threshold;
  const remaining = Math.max(0, next.threshold - lifetime);

  // Show all already-earned badges as a tidy pill row.
  const earned = CONTRIB_BADGES.filter((b) => lifetime >= b.threshold);

  return (
    <div className="mt-2 w-44 ml-auto text-left" data-testid="badge-progress">
      <div className="flex items-baseline justify-between gap-1 mb-0.5">
        <span className="text-[10px] text-neutral-400 truncate">
          {next.icon} {next.label}
        </span>
        <span className={`text-[9px] font-mono ${done ? "text-emerald-400" : "text-neutral-500"}`}>
          {Math.floor(pct)}%
        </span>
      </div>
      <div className="h-1.5 bg-black/60 rounded-full overflow-hidden border border-neutral-800">
        <div
          className={`h-full transition-all duration-700 ${
            done
              ? "bg-gradient-to-r from-emerald-400 to-emerald-300"
              : "bg-gradient-to-r from-amber-500 via-orange-400 to-amber-300"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-[9px] text-neutral-500 mt-0.5 truncate">
        {done
          ? "Badge earned 🎉"
          : `${remaining.toLocaleString()} 💎 to go`}
      </div>
      {earned.length > 0 && (
        <div className="flex flex-wrap gap-0.5 justify-end mt-1">
          {earned.map((b) => (
            <span
              key={b.id}
              title={`${b.label} — ${b.threshold.toLocaleString()}+ 💎 lifetime`}
              className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-emerald-500/20 border border-emerald-400/60 text-emerald-200"
            >
              {b.icon}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}



function MemberRow({ m, isLeader, constants, busy, post, g }) {
  const badge = ROLE_BADGE[m.role] || ROLE_BADGE.member;
  const canManage = isLeader && m.role !== "leader";
  const officerSlotsFull = (g.officer_count || 0) >= (constants?.officer_max ?? 3);

  // 🛡️ Defensive shape fallbacks — never let a missing field blank the page.
  const heroClassLabel = (() => {
    if (!m.hero_class) return null;
    const raw = typeof m.hero_class === "string" ? m.hero_class : (m.hero_class.class_id || "");
    return raw && typeof raw === "string" ? raw.replace("_", " ") : null;
  })();
  const wealth = Number(m.wealth || 0).toLocaleString();
  const contrib = Number(m.wealth_contributed || 0).toLocaleString();
  const displayName = m.name || m.display_name || "Unnamed Hero";

  return (
    <div className="flex items-center gap-3 bg-black/30 border border-neutral-800 rounded-lg p-2 sm:p-3"
         data-testid={`guild-member-${m.user_id}`}>
      <div className="text-xl">{m.pet_icon || "🐦"}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-bold text-neutral-100 truncate">{displayName}</div>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-black uppercase tracking-wider"
                style={{ background: `${badge.color}22`, color: badge.color, border: `1px solid ${badge.color}55` }}>
            {badge.icon} {badge.label}
          </span>
          {heroClassLabel && (
            <span className="text-[9px] text-neutral-500 uppercase tracking-wider">
              {heroClassLabel}
            </span>
          )}
        </div>
        <div className="text-[10px] text-neutral-500">
          💎 {wealth} · Contributed {contrib}
        </div>
      </div>
      {canManage && (
        <div className="flex gap-1 flex-wrap">
          {m.role === "member" && (
            <button type="button" disabled={busy}
                    onClick={() => post("promote", { target_id: m.user_id, role: "assistant_leader" })}
                    className="text-[9px] uppercase tracking-wider font-bold px-2 py-1 bg-purple-900/40 hover:bg-purple-900/70 text-purple-200 rounded">
              → Asst
            </button>
          )}
          {m.role === "member" && !officerSlotsFull && (
            <button type="button" disabled={busy}
                    onClick={() => post("promote", { target_id: m.user_id, role: "officer" })}
                    className="text-[9px] uppercase tracking-wider font-bold px-2 py-1 bg-blue-900/40 hover:bg-blue-900/70 text-blue-200 rounded">
              → Officer
            </button>
          )}
          {(m.role === "officer" || m.role === "assistant_leader") && (
            <button type="button" disabled={busy}
                    onClick={() => post("demote", { target_id: m.user_id })}
                    className="text-[9px] uppercase tracking-wider font-bold px-2 py-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded">
              Demote
            </button>
          )}
          <button type="button" disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Transfer leadership to ${m.name}?`))
                      post("transfer-leadership", { target_id: m.user_id }, `👑 Crown passed.`);
                  }}
                  className="text-[9px] uppercase tracking-wider font-bold px-2 py-1 bg-amber-900/40 hover:bg-amber-900/70 text-amber-200 rounded">
            👑
          </button>
          <button type="button" disabled={busy}
                  onClick={() => {
                    if (window.confirm(`Kick ${m.name}?`)) post("kick", { target_id: m.user_id }, "Kicked.");
                  }}
                  className="text-[9px] uppercase tracking-wider font-bold px-2 py-1 bg-red-900/40 hover:bg-red-900/70 text-red-200 rounded">
            🚪
          </button>
        </div>
      )}
    </div>
  );
}

function ContribLeaderboard({ guild }) {
  const [tab, setTab] = useState("daily"); // daily | lifetime
  const lb = guild?.leaderboard || { daily: [], lifetime: [], daily_day_utc: null };
  const rows = tab === "daily" ? (lb.daily || []) : (lb.lifetime || []);
  return (
    <div className="bg-black/50 border border-amber-900/40 rounded-xl p-4 sm:p-6"
         data-testid="guild-leaderboard-panel">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h3 className="text-lg font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
          🏆 FLOCK CONTRIBUTORS
        </h3>
        <div className="flex gap-1 text-[10px]">
          <button type="button"
                  onClick={() => setTab("daily")}
                  data-testid="guild-lb-tab-daily"
                  className={`px-3 py-1.5 rounded font-bold tracking-wider uppercase transition ${
                    tab === "daily" ? "bg-amber-500 text-black" : "bg-black/40 text-neutral-400 hover:text-neutral-100"}`}>
            Today
          </button>
          <button type="button"
                  onClick={() => setTab("lifetime")}
                  data-testid="guild-lb-tab-lifetime"
                  className={`px-3 py-1.5 rounded font-bold tracking-wider uppercase transition ${
                    tab === "lifetime" ? "bg-amber-500 text-black" : "bg-black/40 text-neutral-400 hover:text-neutral-100"}`}>
            Lifetime
          </button>
        </div>
      </div>
      {tab === "daily" && lb.daily_day_utc && (
        <div className="text-[10px] text-neutral-500 mb-2" data-testid="guild-lb-daily-stamp">
          UTC day: {lb.daily_day_utc} · Rolls over at 00:00 UTC
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-center text-neutral-500 py-8 text-sm" data-testid="guild-lb-empty">
          {tab === "daily"
            ? "No contributions yet today — be the first to feed the flock."
            : "No contributions yet for this guild. Resets on leave/kick."}
        </div>
      ) : (
        <ol className="space-y-1.5">
          {rows.map((r, i) => (
            <li key={r.user_id}
                data-testid={`guild-lb-row-${tab}-${i}`}
                className="flex items-center gap-3 bg-black/30 border border-neutral-800 rounded-lg px-3 py-2">
              <div className={`text-sm font-black w-6 text-center ${
                i === 0 ? "text-amber-300" : i === 1 ? "text-neutral-300" : i === 2 ? "text-amber-700" : "text-neutral-500"}`}>
                {i + 1}
              </div>
              <div className="text-lg">{r.pet_icon || "🐦"}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-neutral-100 truncate flex items-center gap-1.5">
                  {r.name}
                  {(r.badges || []).map(b => (
                    <span key={b.id}
                          title={`${b.label} — ${Number(b.chits).toLocaleString()}+ 🪙 Chits contributed`}
                          className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-amber-500/20 border border-amber-400/60 text-amber-200">
                      {b.icon} {b.label}
                    </span>
                  ))}
                </div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{(r.role || "member").replace("_", " ")}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-black text-purple-300">💎 {Number(r.amount).toLocaleString()}</div>
                <div className="text-[9px] text-neutral-500">🪙 {Number(r.chits_equiv || r.amount * 1000).toLocaleString()}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`px-4 py-2 text-sm font-bold tracking-wider rounded transition ${
              active
                ? "bg-amber-500 text-black"
                : "bg-black/40 text-neutral-400 hover:text-neutral-100"
            }`}>
      {children}
    </button>
  );
}
